/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import * as validator from 'email-validator';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { errors } from '@codegrade/utils';
import { Card } from '@/components/Card';
import CgError from '@/components/Error';
import * as Form from '@/components/Form';
import Loader from '@/components/Loader';
import PasswordSuggestions from '@/components/PasswordSuggestions';
import { PASSWORD_UNIQUE_MESSAGE } from '@/constants';
import { GlobalPermissions as GPerm } from '@/permissions';
import { UserStore } from '@/store';
import * as utils from '@/utils';
export default tsx.component({
    name: 'user-info',
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            name: Maybe.empty(),
            email: Maybe.empty(),
            oldPw: '',
            newPw: '',
            confirmPw: '',
            loading: true,
            error: Maybe.empty(),
        };
    },
    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        canEditInfo() {
            return UserStore.tenantId().mapOrDefault(tid => UserStore.hasTenantPermission()(GPerm.can_edit_own_info, tid), false);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        canEditPw() {
            return UserStore.tenantId().mapOrDefault(tid => UserStore.hasTenantPermission()(GPerm.can_edit_own_password, tid), false);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        username() {
            return UserStore.username();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        formNode() {
            return (<Form.Root data-cy="user-info-form" layout="row" handleSubmit={() => this.submit()} handleReset={() => this.reset()} submitLoadingMessage="Saving user info" submitSuccessMessage="User info saved" submitOnEnter={false} submitOnCtrlOrCommandEnter showReset showSubmit scopedSlots={{
                    formError: err => this.renderError(err),
                }}>
                    {this.usernameNode}
                    {this.fullNameNode}
                    {this.emailNode}
                    {this.oldPasswordNode}
                    {this.newPasswordNode}
                    {this.confirmPasswordNode}
                </Form.Root>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        usernameNode() {
            return (<Form.Field label="Username" description="You cannot change your username.">
                    <Form.Input type="text" value={this.username} readOnly/>
                </Form.Field>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fullNameNode() {
            return (<Form.Field label="Full name">
                    <Form.Input disabled={!this.canEditInfo || this.name.isNothing()} name="full-name" type="text" value={this.name.orDefault('Unknown')} onInput={value => {
                    this.name = Maybe.of(value);
                }} required/>
                </Form.Field>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emailNode() {
            return (<Form.Field label="Email">
                    <Form.Input disabled={!this.canEditInfo || this.email.isNothing()} type="email" name="email" autocomplete="email" value={this.email.orDefault('Unknown')} required onInput={value => {
                    this.email = Maybe.of(value);
                }}/>
                </Form.Field>);
        },
        /** @returns If the email input has changes - different from the server value. */
        isEmailInputDirty() {
            return UserStore.loggedInUser().mapOrDefault(user => !user.email.equals(this.email), false);
        },
        /** @returns The reset password route. */
        resetPasswordRoute() {
            return {
                name: 'forgot',
                params: UserStore.tenantId().mapOrDefault(tenantId => ({ tenantId }), {}),
            };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        oldPasswordNode() {
            return utils.ifOrEmpty(this.canEditPw || this.canEditInfo, () => (<Form.Field>
                    <Form.Label>Current Password</Form.Label>

                    <Form.Input name="old-password" autocomplete="current-password" v-model={this.oldPw} type="password" required={this.isEmailInputDirty || this.newPw !== ''}/>

                    {utils.ifOrEmpty(this.oldPw === '' && this.isEmailInputDirty, () => (<Form.Message semantic="highlighted">
                            Enter your current password to change your email.
                        </Form.Message>))}

                    <Form.Description>
                        If your account was created by using an{' '}
                        <abbr title="Learning Management System, e.g. Canvas or Blackboard" class="cursor-help">
                            LMS
                        </abbr>{' '}
                        it can happen that you don't know this password. If this is the case you can
                        request a password reset link to be sent to your email address on the{' '}
                        <router-link to={this.resetPasswordRoute}>reset password</router-link> page.
                    </Form.Description>
                </Form.Field>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        newPasswordNode() {
            return utils.ifOrEmpty(this.canEditPw, () => (<Form.Field>
                    <Form.Label>New Password</Form.Label>
                    <Form.Input v-model={this.newPw} name="new-password" autocomplete="new-password" type="password"/>
                    {utils.ifOrEmpty(this.newPw !== '', () => (<Form.Message semantic="neutral">{PASSWORD_UNIQUE_MESSAGE}</Form.Message>))}
                </Form.Field>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        confirmPasswordNode() {
            return utils.ifOrEmpty(this.canEditPw, () => (<Form.Field class={{ 'mb-0': !this.canEditInfo && !this.canEditPw }}>
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Input autocomplete="new-password" v-model={this.confirmPw} name="confirm-password" type="password" required={this.newPw !== ''}/>
                </Form.Field>));
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async mounted() {
        this.loading = true;
        try {
            const { user, email } = await UserStore.loggedInUser()
                .filter(u => u.fullyLoaded)
                .orDefaultLazy(() => UserStore.verifyLogin());
            this.resetWithData(user.name, email);
        }
        catch (error) {
            this.error = Maybe.of(errors.coerceToError(error));
        }
        finally {
            this.loading = false;
        }
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        reset() {
            UserStore.loggedInUser().ifJust(({ user, email }) => this.resetWithData(user.name, email));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        resetWithData(name, email) {
            this.name = name;
            this.email = email;
            this.oldPw = '';
            this.newPw = '';
            this.confirmPw = '';
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async submit() {
            if (this.newPw !== this.confirmPw) {
                throw new Error('New password does not match confirm password.');
            }
            if (this.email.mapOrDefault(email => !validator.validate(email), false)) {
                throw new Error('The given email is not valid.');
            }
            try {
                const res = await UserStore.updateUserInfo({
                    name: this.name.extract(),
                    email: this.email.extract(),
                    oldPw: this.oldPw,
                    newPw: this.newPw,
                });
                return res.addCallbacks({
                    onAfterSuccess: ({ body: user }) => this.resetWithData(user.name, user.email),
                });
            }
            catch (error) {
                throw errors.coerceToError(error);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderError(error) {
            if (error instanceof api.ApiResultError &&
                error.error instanceof api.WeakPasswordException) {
                return <PasswordSuggestions error={error.error}/>;
            }
            return utils.getErrorMessage(error);
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        return (<Card header="User profile" class="user-info" bodyClass="@container">
                {this.error.caseOf({
                Just: err => <CgError error={err}/>,
                Nothing: () => utils.ifExpr(this.loading, () => <Loader class="col-md-12 text-center" scale={1}/>, () => this.formNode),
            })}
            </Card>);
    },
});
